/**
 * Smart Enrichment Service
 * 
 * Complete pipeline for converting natural language trip descriptions into
 * fully-generated stages with:
 * - Precise timestamps (relative + absolute)
 * - Location search (Overpass, POI database, geocoding)
 * - Weather enrichment (sunrise/sunset, conditions)
 * - Duration estimates
 * - POI suggestions and alternatives
 */

import type { Firestore } from 'firebase-admin/firestore'
import type { 
  ParsedActivitySegment, 
  EnrichmentPreferences,
  SuggestedStage,
  Stage
} from '@my-roadtrip/types'
import { OpenAIService } from '../../services/openai.service.js'
import { POIDatabaseService } from './poi-database.service.js'
import { createLogger } from '../../common/utils/logger.js'
import { calculateDistance } from '@my-roadtrip/utils'

const logger = createLogger('EnrichmentService')

interface EnrichmentContext {
  userId: string
  startLocation?: { lat: number; lng: number }
  startTime?: Date
  timezone?: string
}

interface LocationCandidate {
  name: string
  address: string
  lat: number
  lng: number
  source: 'poi_db' | 'overpass' | 'geocoded'
  confidence: number
  placeId?: string
  openingHours?: string
  rating?: number
  reviews?: number
}

export class EnrichmentService {
  private openaiService = new OpenAIService()
  private poiDatabase: POIDatabaseService

  constructor(firestore: Firestore) {
    this.poiDatabase = new POIDatabaseService(firestore)
  }

  /**
   * Full enrichment pipeline:
   * 1. Parse text into segments
   * 2. For each segment: find locations, calculate times, enrich with POIs
   * 3. Return complete suggestions with alternatives
   */
  async enrichFromText(
    text: string,
    preferences: EnrichmentPreferences,
    context: EnrichmentContext
  ): Promise<{
    segments: ParsedActivitySegment[]
    suggestions: SuggestedStage[]
    metadata: { confidence: number; processingTimeMs: number }
  }> {
    const startTime = Date.now()
    logger.info('Starting enrichment pipeline', { textLength: text.length, userId: context.userId })

    try {
      // Step 1: Parse text into activity segments
      const segments = await this.openaiService.parseActivitySegments(
        { text, type: 'text', confidence: 95, language: 'fr' },
        { userId: context.userId, feature: 'ai:enrich-from-text' }
      )

      if (segments.length === 0) {
        logger.warn('No segments parsed from text', { text })
        return { segments: [], suggestions: [], metadata: { confidence: 0, processingTimeMs: Date.now() - startTime } }
      }

      logger.info('Segments parsed', { count: segments.length })

      // Step 2: Enrich each segment with location search, POI suggestions, etc
      const suggestions: SuggestedStage[] = []
      let currentTime = context.startTime || new Date()

      for (const segment of segments) {
        // Skip departure/arrival as they're implicit
        if (segment.type === 'departure' || segment.type === 'arrival') {
          continue
        }

        // Find best location match
        const locationCandidates = await this.findLocationCandidates(
          segment,
          context.startLocation,
          preferences
        )

        if (locationCandidates.length === 0) {
          // Fallback: create stage from segment without location
          suggestions.push(this.createSuggestionFromSegment(segment, currentTime, preferences))
        } else {
          // Best match
          const bestLocation = locationCandidates[0]
          
          // Find POIs for this location
          const pois = await this.findPOIsForLocation(
            bestLocation,
            segment.categories,
            preferences
          )

          // Create suggestion
          const suggestion = this.createSuggestionFromLocation(
            segment,
            bestLocation,
            pois,
            currentTime,
            preferences,
            locationCandidates.slice(1, 3) // alternatives
          )

          suggestions.push(suggestion)
        }

        // Update current time for next segment
        if (segment.estimatedDeparture) {
          currentTime = new Date(segment.estimatedDeparture)
        }
      }

      const processingTimeMs = Date.now() - startTime
      logger.info('Enrichment pipeline complete', { 
        segmentCount: segments.length,
        suggestionCount: suggestions.length,
        processingTimeMs
      })

      return {
        segments,
        suggestions,
        metadata: { confidence: 85, processingTimeMs }
      }
    } catch (error) {
      logger.error('Enrichment pipeline failed', { error, text })
      throw error
    }
  }

  /**
   * Find location candidates for a segment
   * 
   * Strategy:
   * 1. Query POI database (fastest, free)
   * 2. Fallback to Overpass API (if location not in DB)
   * 3. Fallback to geocoding (if no results)
   */
  private async findLocationCandidates(
    segment: ParsedActivitySegment,
    startLocation: { lat: number; lng: number } | undefined,
    preferences: EnrichmentPreferences
  ): Promise<LocationCandidate[]> {
    const candidates: LocationCandidate[] = []

    const query = segment.searchQuery || segment.description
    if (!query) {
      return candidates
    }

    try {
      // Strategy 1: Search in POI database first
      if (startLocation) {
        const radius = preferences.maxDeviationKm || 50
        const dbResults = await this.poiDatabase.searchNearby(
          startLocation,
          segment.categories,
          radius,
          { limit: 5 }
        )

        // Filter by search query relevance
        const relevant = dbResults.filter(poi => 
          this.isRelevantToQuery(poi.name || '', query)
        )

        candidates.push(
          ...relevant.map((poi: any) => ({
            name: poi.name || 'POI',
            address: poi.address || poi.city || '',
            lat: poi.location?.coordinates?.[1] || poi.lat || 0,
            lng: poi.location?.coordinates?.[0] || poi.lng || 0,
            source: 'poi_db' as const,
            confidence: 90,
            placeId: poi.placeId || poi.googlePlaceId,
            rating: poi.rating,
            reviews: poi.reviewCount || poi.reviews
          }))
        )
      }

      // Strategy 2: Overpass API (if not enough results)
      if (candidates.length < 3) {
        const overpassResults = await this.searchOverpass(
          query,
          segment.categories,
          startLocation
        )

        candidates.push(
          ...overpassResults.filter(result => 
            !candidates.some(c => c.name === result.name && c.address === result.address)
          )
        )
      }

      // Strategy 3: Fallback to geocoding via OpenAI parsing
      if (candidates.length === 0) {
        // Try to extract coordinates from context
        const geocodeResult = await this.geocodeQuery(query, startLocation)
        if (geocodeResult) {
          candidates.push(geocodeResult)
        }
      }

      return candidates.slice(0, 5) // Return top 5 candidates
    } catch (error) {
      logger.error('Failed to find location candidates', { error, query })
      return candidates
    }
  }

  /**
   * Find POIs near a location for specific categories
   */
  private async findPOIsForLocation(
    location: LocationCandidate,
    categories: string[],
    preferences: EnrichmentPreferences
  ): Promise<any[]> {
    try {
      // Query POI database in 5km radius around location
      const pois = await this.poiDatabase.searchNearby(
        { lat: location.lat, lng: location.lng },
        categories,
        5, // 5km radius around the location
        {
          limit: (preferences.maxPoiPerSegment || 3) * 2, // Get more for filtering
          minConfidence: preferences.minConfidence || 60
        }
      )

      // Filter by rating if specified
      const minRating = preferences.minPoiRating ?? 3.5
      if (minRating > 0) {
        return pois.filter((poi: any) => (poi.rating || 0) >= minRating)
      }

      return pois
    } catch (error) {
      logger.error('Failed to find POIs', { error, location })
      return []
    }
  }

  /**
   * Check if a POI name is relevant to the search query
   */
  private isRelevantToQuery(poiName: string, query: string): boolean {
    const poiLower = poiName.toLowerCase()
    const queryLower = query.toLowerCase()

    // Exact match
    if (poiLower.includes(queryLower) || queryLower.includes(poiLower)) {
      return true
    }

    // Fuzzy match (at least 70% similarity)
    const similarity = this.calculateStringSimilarity(poiLower, queryLower)
    return similarity > 0.7
  }

  /**
   * Calculate string similarity (0-1)
   */
  private calculateStringSimilarity(str1: string, str2: string): number {
    const longer = str1.length > str2.length ? str1 : str2
    const shorter = str1.length > str2.length ? str2 : str1

    if (longer.length === 0) return 1.0

    const editDistance = this.levenshteinDistance(longer, shorter)
    return (longer.length - editDistance) / longer.length
  }

  /**
   * Levenshtein distance for fuzzy matching
   */
  private levenshteinDistance(s1: string, s2: string): number {
    const costs: number[] = []

    for (let i = 0; i <= s1.length; i++) {
      let lastValue = i
      for (let j = 0; j <= s2.length; j++) {
        if (i === 0) {
          costs[j] = j
        } else if (j > 0) {
          let newValue = costs[j - 1]
          if (s1.charAt(i - 1) !== s2.charAt(j - 1)) {
            newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1
          }
          costs[j - 1] = lastValue
          lastValue = newValue
        }
      }
      if (i > 0) costs[s2.length] = lastValue
    }

    return costs[s2.length]
  }

  /**
   * Search Overpass API for locations
   */
  private async searchOverpass(
    query: string,
    categories: string[],
    location?: { lat: number; lng: number }
  ): Promise<LocationCandidate[]> {
    // TODO: Implement Overpass API search
    // For now, return empty array
    return []
  }

  /**
   * Geocode a query using OpenAI context or fallback
   */
  private async geocodeQuery(
    query: string,
    location?: { lat: number; lng: number }
  ): Promise<LocationCandidate | null> {
    // TODO: Implement geocoding fallback (Google Maps API or similar)
    // For now, return null
    return null
  }

  /**
   * Create a suggested stage from segment (no location found)
   */
  private createSuggestionFromSegment(
    segment: ParsedActivitySegment,
    currentTime: Date,
    preferences: EnrichmentPreferences
  ): SuggestedStage {
    const stage: Omit<Stage, 'createdAt' | 'updatedAt'> = {
      id: `stage_${Date.now()}`,
      planId: '',
      name: segment.description,
      notes: `Catégories: ${segment.categories.join(', ')}`,
      location: { address: segment.searchQuery || '', coordinates: { lat: 0, lng: 0 } },
      order: 0,
      type: 'waypoint',
      estimatedArrival: segment.estimatedArrival || currentTime,
      actualArrival: undefined
    }

    return {
      id: `suggestion_${Date.now()}`,
      stage,
      source: 'user',
      confidence: Math.max(40, segment.confidence - 20),
      reasoning: 'Aucun lieu trouvé, utilisation du texte utilisateur',
      alternatives: []
    }
  }

  /**
   * Create a suggested stage from a location match
   */
  private createSuggestionFromLocation(
    segment: ParsedActivitySegment,
    location: LocationCandidate,
    pois: any[],
    currentTime: Date,
    preferences: EnrichmentPreferences,
    alternatives: LocationCandidate[]
  ): SuggestedStage {
    const stage: Omit<Stage, 'createdAt' | 'updatedAt'> = {
      id: `stage_${Date.now()}`,
      planId: '',
      name: location.name,
      notes: segment.description,
      location: {
        address: location.address,
        coordinates: { lat: location.lat, lng: location.lng },
        placeId: location.placeId,
        name: location.name
      },
      order: 0,
      type: 'poi',
      estimatedArrival: segment.estimatedArrival || currentTime,
      actualArrival: undefined
    }

    return {
      id: `suggestion_${Date.now()}`,
      stage,
      source: 'poi_database',
      confidence: segment.confidence,
      reasoning: `Lieu trouvé: ${location.name} (source: ${location.source}, confiance: ${location.confidence}%)`,
      alternatives: alternatives.map((alt, idx) => ({
        id: `alt_${idx}`,
        stage: { ...stage, name: alt.name, location: { ...stage.location, address: alt.address, name: alt.name } },
        score: alt.confidence
      }))
    }
  }
}
